import { useCallback, useEffect, useRef, useState } from "react"
import { LoaderCircle } from "lucide-react"
import { useParams, useSearchParams } from "react-router-dom"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { ChatWidget } from "@/components/widget/chat-widget"
import { api } from "@/lib/api"
import type { PublicWidgetSettings } from "@/lib/types"

export function EmbedPage() {
  const { publicKey = "" } = useParams()
  const [searchParams] = useSearchParams()
  const sessionToken = searchParams.get("token") ?? ""
  const [settings, setSettings] = useState<PublicWidgetSettings>()
  const [error, setError] = useState<string>()

  useEffect(() => {
    const previous = document.body.style.background
    document.body.style.background = "transparent"
    document.documentElement.style.background = "transparent"
    if (!sessionToken) setError("Sessão do widget ausente. Recarregue o site onde ele foi instalado.")
    else api.publicConfig(publicKey, sessionToken).then(setSettings).catch((caught) => setError(caught instanceof Error ? caught.message : "Assistente indisponível."))
    return () => { document.body.style.background = previous }
  }, [publicKey, sessionToken])

  const openRef = useRef(false)

  const resize = useCallback((open: boolean) => {
    window.parent.postMessage({
      type: "bubbledesk:resize", open, position: settings?.position,
      launcherSize: settings?.launcherSize, widgetSize: settings?.widgetSize,
      windowWidth: settings?.windowWidth, windowHeight: settings?.windowHeight,
      motionStyle: settings?.motionStyle,
    }, "*")
  }, [settings])

  const handleOpenChange = useCallback((open: boolean) => {
    openRef.current = open
    resize(open)
  }, [resize])

  const handleBalloonChange = useCallback((visible: boolean, type?: "callout" | "preview" | null, style?: { backgroundColor: string; background: string; textColor: string; borderRadius: number; capsuleRadius: number }) => {
    window.parent.postMessage({
      type: "bubbledesk:callout",
      visible: visible && type === "callout",
      title: settings?.calloutTitle ?? "",
      subtitle: settings?.calloutSubtitle ?? "",
      position: settings?.position,
      launcherSize: settings?.launcherSize,
      color: style?.backgroundColor,
      background: style?.background,
      textColor: style?.textColor,
      borderRadius: style?.borderRadius,
      capsuleRadius: style?.capsuleRadius,
    }, "*")
  }, [settings])

  if (error) return <div className="fixed bottom-3 right-3 w-[min(350px,calc(100vw-24px))]"><Alert variant="destructive"><AlertTitle>Widget indisponível</AlertTitle><AlertDescription>{error}</AlertDescription></Alert></div>
  if (!settings) return <div className="fixed bottom-6 right-6 flex size-14 items-center justify-center rounded-2xl bg-white shadow-lg"><LoaderCircle className="size-5 animate-spin text-primary" /></div>

  return <ChatWidget settings={settings} publicKey={publicKey} sessionToken={sessionToken} launcherOnlyWhenClosed onOpenChange={handleOpenChange} onBalloonChange={handleBalloonChange} />
}
